import { get } from 'lodash';

import { NotificationType } from '@swf/components/StarsMPNotification/enums';
import { getTransformedInputData as transformMPNotification } from '@swf/components/StarsMPNotification/utils';
import { Brand, FeatureFlag, Site, SuspendAccountCode } from '@swf/core/enums';
import type { EffectParams, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { Account } from '@swf/core/types/Account';
import { License } from '@swf/core/types/Account';
import type {
  BaseMPNotification,
  ChestRewardMPNotification,
  DisplayValuePropositionNotification,
  FixedPrizeDrawIssuedMPNotification,
  GameTicketMPNotification,
  GeoComplyPLC,
  InactivityMessage,
  InactivityNotification,
  LobbyNotification,
  LoginEventNotification,
  Notification,
  RawNotification,
  SuspendAccount
} from '@swf/core/types/Notification';
import { NotificationCode } from '@swf/core/types/Notification';

import { geoComplyPLC } from './geoComplyPLC/geoComplyPLC';
import type { NotificationMessage } from './index';
import { LoginEventType, TokenInvalidReason } from './index';

const geoComplyVerified = 1;

const parseNotification = (rawNotification: RawNotification): Notification => {
  if (rawNotification) {
    const { clearbets, rollid, notificationid } = rawNotification;
    return {
      ...rawNotification,
      ...(clearbets ? { clearBets: clearbets } : {}),
      ...(rollid ? { rollId: rollid } : {}),
      ...(notificationid ? { notificationId: notificationid } : {})
    };
  }
  return rawNotification;
};

/**
 * For multipurpose notifications, get identifier, notification type and feature flag enabled
 * @param notification
 */
const getMPNotificationInfo = (notification: BaseMPNotification, rootState: RootState) => {
  const { type, state = 'issued' } = notification;
  let featureEnabledFlag = FeatureFlag.WebNotificationEnabled;
  let ticketId = '';

  if (type === NotificationType.MiniGameTicket) {
    ticketId = `gameTicket-${(notification as GameTicketMPNotification).attributes.ticketId}`;
    featureEnabledFlag = FeatureFlag.WebNotificationEnabled;
  } else if (type === NotificationType.FixedPrizeDrawBase) {
    ticketId = `fixedPrizeDrawTicket-${
      (notification as FixedPrizeDrawIssuedMPNotification).attributes.drawId
    }`;
    featureEnabledFlag = FeatureFlag.FixedPrizeDrawNotificationEnabled;
  } else if (type === NotificationType.ChestRewardBase) {
    ticketId = `chestRewardTicket-${
      (notification as ChestRewardMPNotification).attributes.chestId
    }`;
    featureEnabledFlag = FeatureFlag.WebMPCNotificationEnabled;
  }

  const isNotificationEnabled = select.feature.check(rootState, featureEnabledFlag);

  return { ticketId, state, isNotificationEnabled };
};

const effects = {
  async push(
    { headers: { type }, body }: NotificationMessage,
    { rootState, dispatch, request }: EffectParams
  ) {
    const notification = parseNotification(body.notification as RawNotification);
    const ticketRollId = `gameTicket-${get(notification, ['gameTicketId'], '')}`;
    const isEmbedded = select.site.isEmbedded(rootState);
    const newGameTicketEnabled = select.feature.check(
      rootState,
      FeatureFlag.WebNotificationEnabled
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const enqueue = (params: { path: string; locationState?: Record<string, any> }) =>
      dispatch.modal.enqueue({ ...params });

    switch (type) {
      case 'gameticket.expiring':
        if (!isEmbedded && !select.notification.getExpiringById(rootState, ticketRollId)) {
          dispatch.notification.addExpiring({ rollId: ticketRollId } as Notification);
          enqueue({
            path: '/notifications/game-ticket/expiring',
            locationState: { ...notification, ticketRollId }
          });
        }
        break;
      case 'gameticket.issued':
        if (
          !isEmbedded &&
          !select.notification.getIssuedById(rootState, ticketRollId) &&
          !newGameTicketEnabled
        ) {
          dispatch.notification.addIssued({ rollId: ticketRollId } as Notification);
          enqueue({
            path: '/notifications/game-ticket/issued',
            locationState: { ...notification, ticketRollId }
          });
        }
        break;
      case 'multipurpose.notification': {
        const notificationData = notification as BaseMPNotification;
        const { isNotificationEnabled } = getMPNotificationInfo(notificationData, rootState);

        if (!isEmbedded && isNotificationEnabled) {
          enqueue({
            path: '/mpnotifications',
            locationState: transformMPNotification(notificationData, select.locale.get(rootState))
          });
        }
        break;
      }
      case 'notification.tokenExpireWarning': {
        const currentModal = select.modal.getCurrent(rootState);
        if (currentModal?.path.includes('/eula')) {
          break;
        }
        const { remainingInactivitySeconds } = select.localSettings.get(rootState);
        const { remainingSeconds } = notification as InactivityNotification;

        if (remainingInactivitySeconds && remainingSeconds) {
          setTimeout(async () => {
            const {
              body: { remainingSeconds: remainingTime }
            } = await request({ service: 'account.token.expiryTime' });
            if (remainingTime <= remainingInactivitySeconds) {
              dispatch.modal.unshift({
                path: '/notifications/inactivity-timeout-warning',
                locationState: {
                  ...notification,
                  remainingSeconds: remainingInactivitySeconds
                }
              });
            }
          }, (remainingSeconds - remainingInactivitySeconds) * 1000);
          break;
        } else {
          const path = [License.EU, License.COM, License.GR, License.USMI, License.USPA].includes(
            select.account.getLicense(rootState)
          )
            ? '/notifications/inactivity-timeout-warning'
            : '/notifications/inactivity-warning';

          dispatch.modal.unshift({ path, locationState: notification });
          break;
        }
      }
      case 'session.logout.reply': {
        const { reasonCode } = body as InactivityMessage;
        await dispatch.auth.logout({ sessionExpired: true });
        switch (reasonCode) {
          case TokenInvalidReason.EXPLICIT_LOGOUT:
          case TokenInvalidReason.PLAYING_TIME_REACHED:
            // do nothing
            break;
          case TokenInvalidReason.NOT_ALLOWED_TO_PLAY:
            enqueue({
              path: '/notifications/play-session-limit-gr',
              locationState: { msg: (body as InactivityMessage).text }
            });
            break;
          case TokenInvalidReason.EXPIRE:
            enqueue({
              path: '/notifications/inactivity-logout',
              // message For Victor by Levon
              // TODO: as per Igor Baranov body for session.logout.reply with reasonCode 1 will always come as {idleSeconds: ###, reasonCode: 1}
              // so on line 54 we pass undefiined to parseNotification and get undefined from it.
              // This is fallback solution which need to be removed and message shape fixed on WMT
              locationState: notification || {
                idleSeconds: (body as InactivityMessage).idleSeconds
              }
            });
            break;
          case TokenInvalidReason.ADMIN_FORCE_LOGOUT:
          default:
            enqueue({ path: '/notifications/force-logout' });
        }
        break;
      }
      case 'notification.account.info':
        dispatch.account.update(body.notification as Account);
        break;
      case 'mpc.issued': {
        if (!select.feature.check(rootState, FeatureFlag.WebMPCNotificationEnabled)) {
          enqueue({ path: '/notifications/chest', locationState: notification });
        }
        break;
      }
      case 'cvl.state.notification':
        enqueue({ path: '/notifications/chest-tier', locationState: notification });
        break;
      case 'freespin.expiring':
        if (!isEmbedded && !select.notification.getExpiringById(rootState, +notification.rollId)) {
          dispatch.notification.addExpiring(notification);
          enqueue({
            path: '/mpnotifications',
            locationState: {
              ...notification,
              type: NotificationType.FreeSpinsExpiring
            }
          });
        }
        break;
      case 'freespin.issued':
        if (!isEmbedded && !select.notification.getIssuedById(rootState, +notification.rollId)) {
          dispatch.notification.addIssued(notification);
          enqueue({
            path: '/mpnotifications',
            locationState: {
              ...notification,
              type: NotificationType.FreeSpinsIssued
            }
          });
        }
        break;
      case 'userroll.issued':
        if (!isEmbedded && !select.notification.getIssuedById(rootState, +notification.rollId)) {
          dispatch.notification.addIssued(notification);
          enqueue({
            path: '/mpnotifications',
            locationState: {
              ...notification,
              type: NotificationType.BonusIssued
            }
          });
        }
        break;
      case 'userroll.expiring':
        if (!isEmbedded && !select.notification.getExpiringById(rootState, +notification.rollId)) {
          dispatch.notification.addExpiring(notification);
          enqueue({
            path: '/mpnotifications',
            locationState: {
              ...notification,
              type: NotificationType.BonusExpiring
            }
          });
        }
        break;
      case 'userroll.cleared':
        if (!isEmbedded && !select.notification.getClearedById(rootState, +notification.rollId)) {
          dispatch.notification.addCleared(notification);
          enqueue({
            path: '/mpnotifications',
            locationState: {
              ...notification,
              type: NotificationType.BonusCleared
            }
          });
        }
        break;
      case 'notification.session.duration':
        // @TODO when RO license is on the web check why WMT sends this message before set time
        if (!isEmbedded) {
          enqueue({
            path: '/notifications/session-duration',
            locationState: notification
          });
        }
        break;
      case 'notification.lobby.msg':
        if (!isEmbedded) {
          const siteId = select.site.getId(rootState);
          const isSiteUS = select.site.isSiteUS(rootState);
          const brand = select.site.getBrand(rootState);
          const lobbyNotification = body.notification as LobbyNotification;

          switch (true) {
            case brand === Brand.PokerStarsVegas &&
              lobbyNotification.notificationCode === NotificationCode.SessionInfo:
              dispatch.modal.close('/notifications/hourly-notification-de');
              dispatch.modal.unshift({
                path: '/notifications/hourly-notification-de',
                locationState: notification
              });
              break;
            case siteId === Site.GR:
              dispatch.localSettings.set({ playSessionLimitGR: notification });
              dispatch.modal.unshift({
                path: '/notifications/play-session-limit-gr',
                locationState: notification
              });
              break;
            case [Site.SE, Site.NL].includes(siteId):
              dispatch.modal.close('/notifications/logout-restrictions');
              dispatch.modal.unshift({
                path: '/notifications/logout-restrictions',
                locationState: notification
              });
              break;
            case isSiteUS:
              dispatch.modal.close('/notifications/restrict-playing-time');
              dispatch.localSettings.set({ restrictPlayingTime: notification });
              dispatch.modal.unshift({
                path: '/notifications/restrict-playing-time',
                locationState: notification
              });
              break;
            default:
              enqueue({ path: '/notifications/simple-notification', locationState: notification });
              break;
          }
        }
        break;
      case 'notification.session.summary':
        if (!isEmbedded) {
          const { path } = select.modal.getCurrent(rootState) || {};
          if (path === '/notifications/session-summary-notification') {
            dispatch.modal.close('/notifications/session-summary-notification');
          }

          const { notificationId } = body as RawNotification;
          dispatch.localSettings.set({ sessionSummary: { ...notification, notificationId } });
          dispatch.modal.unshift({
            path: '/notifications/session-summary-notification',
            locationState: { ...notification, notificationId }
          });
        }
        break;
      case 'notification.login.event': {
        const loginNotification = body.notification as LoginEventNotification;
        const license = select.account.getLicense(rootState);
        const ITLicense = license === License.IT;

        switch (loginNotification.event) {
          case ITLicense && LoginEventType.ValidateAccountReminder:
          case ITLicense && LoginEventType.SuspendedDocumentsSubmission:
          case ITLicense && LoginEventType.AccountVerification:
            dispatch.modal.unique({
              path: '/notifications/documents-submission',
              locationState: { ...loginNotification }
            });
            break;
          case LoginEventType.ValidateAccountReminder:
            if (license === License.DK) {
              dispatch.modal.unique({
                path: '/notifications/validate-account-reminder',
                locationState: loginNotification
              });
            } else if (license === License.RO) {
              dispatch.localSettings.set({ validateAccountDaysRO: loginNotification.param });
              dispatch.modal.unique({
                path: '/notifications/validate-account-reminder-ro',
                locationState: loginNotification
              });
            }
            break;
          case LoginEventType.AccountVerification:
            dispatch.modal.unique({ path: '/notifications/account-verification' });
            break;
        }
        break;
      }
      case 'notification.displayValueProposition':
        if (!isEmbedded) {
          const { selfExclusionExpirationDate } =
            notification as DisplayValuePropositionNotification;

          if (selfExclusionExpirationDate) {
            dispatch.account.update({ selfExclusionExpirationDate });
          }

          enqueue({ path: '/notifications/value-proposition/before-you-begin' });
        }
        break;
      case 'notification.player.blacklisted': {
        const { code } = body.notification as SuspendAccount;
        switch (code) {
          case SuspendAccountCode.Blacklisted:
            dispatch.modal.replace([
              {
                path: '/notifications/suspended-account',
                locationState: { code }
              }
            ]);
            break;
          case SuspendAccountCode.Suspended:
          case SuspendAccountCode.ExclusionList:
            dispatch.modal.enqueue({
              path: '/notifications/suspended-account',
              locationState: { code }
            });
            break;
        }
        break;
      }
      case 'notification.geocomply.locate': {
        geoComplyPLC(body.notification as GeoComplyPLC);
        break;
      }
      case 'notification.geoip.result':
        if ((body.notification as GeoComplyPLC).result !== geoComplyVerified) {
          enqueue({ path: '/notifications/geo-comply', locationState: notification });
        }
        break;
      case 'notification.session.forcelogout': {
        const siteId = select.site.getId(rootState);
        await dispatch.auth.logout({});
        if (siteId === Site.GR) {
          dispatch.localSettings.set({ playSessionLimitGR: notification });
          enqueue({
            path: '/notifications/play-session-limit-gr',
            locationState: notification
          });
        } else if (siteId === Site.CZ) {
          enqueue({ path: '/notifications/admin-info-notification', locationState: notification });
        }
        break;
      }
      case 'notification.admin.info': {
        enqueue({ path: '/notifications/admin-info-notification', locationState: notification });
        break;
      }
    }
  },
  async acceptBonus(
    payload: {
      acceptBonus: boolean;
      rollId: number | string;
      status: number;
      notificationId: number;
    },
    { dispatch, request }: EffectParams
  ) {
    let hasError = false;
    const params = {
      service: payload.acceptBonus ? 'notification.roll.accept' : 'notification.roll.decline',
      body: {
        rollid: payload.rollId,
        status: payload.status,
        notificationid: payload.notificationId
      }
    };

    dispatch.notification.addIssued({ rollId: payload.rollId, submitted: true } as Notification);

    try {
      const { body } = await request(params);
      if (!body.success) {
        hasError = true;
      }
    } catch (error) {
      hasError = true;
    }

    dispatch.notification.addIssued({
      rollId: payload.rollId,
      hasError,
      submitted: false
    } as Notification);
  },

  delete(
    {
      notificationId
    }: {
      notificationId?: number;
    },
    { request }: EffectParams
  ) {
    request({
      service: 'notification.delete',
      body: { notificationId }
    });
  },

  async acceptGameTicket(
    {
      gameTicketId,
      status,
      notificationId,
      rollId,
      acceptTicket
    }: {
      rollId: string;
      acceptTicket: boolean;
      gameTicketId: number;
      notificationId: number;
      status: number;
    },
    { request, dispatch }: EffectParams
  ) {
    let hasError = false;
    const params = {
      service: acceptTicket ? 'notification.gameticket.accept' : 'notification.gameticket.decline',
      body: { gameTicketId, status, notificationId }
    };

    dispatch.notification.addIssued({ rollId, submitted: true } as Notification);

    try {
      const { body } = await request(params);
      hasError = !body.success;

      dispatch.modal.close('/notifications');
    } catch (error) {
      hasError = true;
    }

    dispatch.notification.addIssued({ rollId, hasError, submitted: false } as Notification);
  },
  async setPendingNotifications(params: undefined, { rootState, dispatch }: EffectParams) {
    if (select.account.isCasinoRealityCheckRequired(rootState)) {
      dispatch.modal.unique({ path: '/notifications/casino-session-reminder' });
    }
    if (!select.account.isEmailValidated(rootState)) {
      dispatch.modal.unique({ path: '/notifications/confirm-email' });
    }
    if (select.account.getForcedCashoutEnabled(rootState)) {
      dispatch.modal.unique({ path: '/notifications/login-cash-out' });
    }
    if (
      select.account.getDepositConfirmLimits(rootState) ||
      select.account.getGameConfirmLimits(rootState)
    ) {
      dispatch.modal.unique({ path: '/notifications/login-limits' });
    }
  },
  async getNotificationCounters(params: undefined, { dispatch, request }: EffectParams) {
    try {
      const { body } = await request({ service: 'notification.mystars.getcounters' });
      dispatch.notification.addCounter(body);
    } catch (error) {
      // do nothing
    }
  }
};

export default effects;
